import {HttpHandlerFn, HttpInterceptorFn, HttpRequest} from '@angular/common/http';
import {inject, Injector, Signal} from '@angular/core';
import {HTTP_OPTION_KEYS, HttpRequestConfig, IHttpRequestConfig, ILoginResponse} from '@core/api/data-access';
import {BehaviorSubject, catchError, EMPTY, filter, isObservable, Observable, switchMap, take, throwError} from 'rxjs';
import {AuthStore} from '../../+store/auth.store';
import {RefreshLoginRequestModel} from '../../models/login';
import {AuthService} from '../../services';
import {ReAuthenticateService} from './re-authenticate-dialog/re-authenticate-user';

export function TokenInterceptor(): HttpInterceptorFn {
	let isRefreshing = false;
	const refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	return (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
		const authService: AuthService = inject(AuthService);
		const authStore: AuthStore = inject(AuthStore);
		const injector: Injector = inject(Injector);
		const reAuthService: ReAuthenticateService = inject(ReAuthenticateService);

		// if the accessToken is '' and the  request requires a token => block the request and return an empty observable
		if (authStore.accessToken() === '' && HttpRequestConfig.getRequestConfig(req)[HTTP_OPTION_KEYS.appendToken]) return EMPTY;

		const blockInterceptor: Signal<boolean> = authStore.isAdmin;

		const config: IHttpRequestConfig = HttpRequestConfig.getRequestConfig(req);
		req = HttpRequestConfig.getPureRequest(req);
		if (config[HTTP_OPTION_KEYS.appendToken] && !blockInterceptor()) {
			req = appendToken(req, authStore.accessToken());
		}

		return next(req).pipe(
			catchError(error => {
				if (error && error.status === 401) {
					if (!isRefreshing) {
						isRefreshing = true;
						refreshTokenSubject.next(null);

						const refreshTokenRequest: RefreshLoginRequestModel = new RefreshLoginRequestModel({
							accessToken: authStore.accessToken(),
							refreshToken: authStore.refreshToken(),
						});
						// if the refresh token is invalid, logout user
						if (refreshTokenRequest?.isValid() === false) return logoutUser(injector);
						// else, refresh the token
						return authService.login(refreshTokenRequest).pipe(
							// if refresh token is successful, update the token and retry the request
							switchMap((refreshResponse: ILoginResponse) => {
								isRefreshing = false;
								authStore.onLoginSuccess(refreshResponse);
								refreshTokenSubject.next(refreshResponse.accessToken);
								return next(appendToken(req, refreshResponse.accessToken));
							}),
							// if the refresh token fails, try to re-authenticate the user
							catchError(() => {
								if (reAuthService.canReAuth$()) {
									isRefreshing = false;
									const reAuth = reAuthService.openReAuthenticateDialog()?.pipe(
										// if user re-authenticates, retry the request
										switchMap((response: ILoginResponse | null) => {
											if (!response) throw error;
											authStore.onLoginSuccess(response);
											return next(appendToken(req, response.accessToken));
										}),
										// if user cancels re-authentication, logout
										catchError(() => logoutUser(injector)),
									);
									return isObservable(reAuth) ? reAuth : EMPTY;
								} else return logoutUser(injector);
							}),
						);
					} else {
						return refreshTokenSubject.pipe(
							filter(Boolean),
							take(1),
							switchMap(token => {
								return next(appendToken(req, token));
							}),
						);
					}
				}
				return throwError(() => error);
			}),
		);
	};

	function appendToken(req: HttpRequest<any>, accessToken: string): HttpRequest<any> {
		return req.clone({setHeaders: {Authorization: `Bearer ${accessToken}`}});
	}

	function logoutUser(injector: Injector): Observable<never> {
		const authStore: AuthStore = injector.get(AuthStore);
		authStore.logout();
		return EMPTY;
	}
}
